import React, { useState, useRef, useEffect, Suspense, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import i18next from 'i18next';
import { contrastOptions } from '../../constants/UIConstants';
import sessionConstants from '../../constants/SessionConstants';
import { t } from '../../services/i18nService';
import DrawingCanvasService from '../../services/DrawingCanvasService';
import postMessageService from '../../services/PostMessageService';
import DrawingDeleteConfirmModal from './DrawingDeleteConfirmModal';
import excalidrawPreloadService from '../../services/ExcalidrawPreloadService';
import storeManager from '../../store/StoreManager';
import alertService from '../../services/AlertService';
import AlertLevel from '../../components/alert/AlertLevel';
import settingsReader from '../../store/readHelpers/SettingsReader';
import { restoreLockedBackgroundElements } from '../../services/DrawingUtils';
import styles from './DrawingBlock.scss';


const isDrawingEnabled = config => !!config?.enabled;
const NORMAL_FONT_ID = 2;

function useDrawingBlockState() {
    const [hasDrawing, setHasDrawing] = useState(false);
    const [showExcalidraw, setShowExcalidraw] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    const [drawingData, setDrawingData] = useState(null);
    const [drawingImageUrl, setDrawingImageUrl] = useState(null);
    const [isSaving, setIsSaving] = useState(false);
    const [isSavingLocally, setIsSavingLocally] = useState(false);
    const [isLazyExcalidrawLoaded, setIsLazyExcalidrawLoaded] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [excalidrawComponent, setExcalidrawComponent] = useState(null);
    const [isEdited, setIsEdited] = useState(false);
    const [showAutoSaveIndicator, setShowAutoSaveIndicator] = useState(false);

    return {
        hasDrawing,
        setHasDrawing,
        showExcalidraw,
        setShowExcalidraw,
        showPreview,
        setShowPreview,
        drawingData,
        setDrawingData,
        drawingImageUrl,
        setDrawingImageUrl,
        isSaving,
        setIsSaving,
        isSavingLocally,
        setIsSavingLocally,
        isLazyExcalidrawLoaded,
        setIsLazyExcalidrawLoaded,
        showDeleteModal,
        setShowDeleteModal,
        excalidrawComponent,
        setExcalidrawComponent,
        isEdited,
        setIsEdited,
        showAutoSaveIndicator,
        setShowAutoSaveIndicator,
    };
}

const DrawingBlock = ({
    question,
    userResponses,
    handleUserResponse,
    services,
    isReadOnly = false,
    onCanvasStateChange,
    contrastOption = contrastOptions.NORMAL,
    language: sessionLanguage,
    context,
    shouldAutoOpenCanvas = false,
    showQuestionToggle = null,
    questionContent = null,
}) => {
    const isGradingQuestionPreviewContext = context === sessionConstants.CONTEXT_VIEW_GRADING_QUESTION_PREVIEW;
    const canShowCanvasBackgroundByContext = context === sessionConstants.CONTEXT_PREVIEW
        || context === sessionConstants.CONTEXT_PREVIEW_DRAFT
        || context === sessionConstants.CONTEXT_EXAM
        || isGradingQuestionPreviewContext;

    // Check for authorDrawingBgEdit mode from URL parameter
    const isAuthorDrawingBgEditMode = useMemo(() => {
        return DrawingCanvasService.isAuthorDrawingBgEditMode();
    }, []);

    const {
        hasDrawing, setHasDrawing,
        showExcalidraw, setShowExcalidraw,
        showPreview, setShowPreview,
        drawingData, setDrawingData,
        drawingImageUrl, setDrawingImageUrl,
        isSaving, setIsSaving,
        isSavingLocally, setIsSavingLocally,
        isLazyExcalidrawLoaded, setIsLazyExcalidrawLoaded,
        showDeleteModal, setShowDeleteModal,
        excalidrawComponent, setExcalidrawComponent,
        isEdited, setIsEdited,
        showAutoSaveIndicator, setShowAutoSaveIndicator,
    } = useDrawingBlockState();

    const excalidrawRef = useRef(null);
    const canvasContainerRef = useRef(null);
    const initialSnapshotRef = useRef(null);
    const pendingDrawingDataRef = useRef(null);
    const canvasBackgroundElementsRef = useRef(null);
    const skipLockedBgRepairOnceRef = useRef(false);
    const didAutoShowBackgroundPreviewRef = useRef(false);
    const didAutoShowDrawingPreviewRef = useRef(false);

    const setPendingDrawingScene = useCallback((sceneData) => {
        pendingDrawingDataRef.current = {
            elements: sceneData.elements,
            appState: sceneData.appState || {},
            files: sceneData.files || {},
        };
    }, []);

    const isDarkModeEnabled = DrawingCanvasService.shouldApplyDarkMode(contrastOption);

    const drawingConfig = useMemo(() => {
        const config = DrawingCanvasService.getDrawingConfig(question);
        return config;
    }, [question]);

    const extraResponses = useMemo(() => {
        const responses = DrawingCanvasService.getExtraResponses(userResponses, drawingConfig);
        return responses;
    }, [userResponses, drawingConfig]);


    const loadExcalidraw = useCallback(async (shouldShowCanvas = true) => {
        if (!isLazyExcalidrawLoaded) {
            const component = await excalidrawPreloadService.getExcalidrawComponent();
            setExcalidrawComponent(component);
            setIsLazyExcalidrawLoaded(true);
            if (shouldShowCanvas) {
                setShowExcalidraw(true);
                setShowPreview(false);
                if (onCanvasStateChange) {
                    onCanvasStateChange(true);
                }
            }
        } else if (shouldShowCanvas) {
            setShowExcalidraw(true);
            setShowPreview(false);
            if (onCanvasStateChange) {
                onCanvasStateChange(true);
            }
        }
    }, [isLazyExcalidrawLoaded, onCanvasStateChange]);

    // IB-14772 - Temp solution: Ensure the help tooltip is set for the help icon
    const ensureHelpTooltip = useCallback(() => {
        if (typeof document === 'undefined') {
            return;
        }
        const helpButtons = document.querySelectorAll('.help-icon');

        if (!helpButtons || Array.from(helpButtons).length === 0) {
            return;
        }

        helpButtons.forEach((button) => {
            button.setAttribute('title', 'Help — ?');
        });
    }, []);

    const handleExcalidrawReady = useCallback(() => {
        const dataToUse = drawingData || pendingDrawingDataRef.current?.elements;
        if (dataToUse && excalidrawRef.current) {
            const pendingAppState = pendingDrawingDataRef.current?.appState || {};
            const sanitizedAppState = { ...pendingAppState };
            if (sanitizedAppState.collaborators !== undefined && !Array.isArray(sanitizedAppState.collaborators)) {
                delete sanitizedAppState.collaborators;
            }
            excalidrawRef.current.updateScene({
                elements: dataToUse,
                appState: {
                    ...sanitizedAppState,
                    currentItemRoughness: 0,
                    currentItemFontFamily: NORMAL_FONT_ID,
                },
            });
            if (pendingDrawingDataRef.current?.files) {
                excalidrawRef.current.addFiles(Object.values(pendingDrawingDataRef.current.files));
            }
            pendingDrawingDataRef.current = null;
        }

        if (contrastOption === contrastOptions.YELLOW_ON_BLACK && excalidrawRef.current) {
            excalidrawRef.current.updateScene({
                appState: {
                    currentItemStrokeColor: '#ffff00',
                    currentItemRoughness: 0,
                    currentItemFontFamily: NORMAL_FONT_ID,
                },
            });
        }

        // Capture initial snapshot after scene is ready
        const snapshot = DrawingCanvasService.captureSnapshot(excalidrawRef);
        if (snapshot) {
            initialSnapshotRef.current = snapshot;
            setIsEdited(false);
            isEditedRef.current = false;
        }
    }, [drawingData, isDarkModeEnabled, contrastOption]);

    const handleExcalidrawChange = useCallback((elements, appState, files) => {
        if (!excalidrawRef.current) {
            return;
        }

        // Candidate mode: prevent locked canvas-background elements from being deleted.
        if (!isAuthorDrawingBgEditMode && !isReadOnly) {
            const bgElements = canvasBackgroundElementsRef.current;
            if (Array.isArray(bgElements) && bgElements.length > 0) {
                if (skipLockedBgRepairOnceRef.current) {
                    skipLockedBgRepairOnceRef.current = false;
                } else {
                    const repaired = restoreLockedBackgroundElements(elements || [], bgElements);
                    if (repaired) {
                        skipLockedBgRepairOnceRef.current = true;
                        excalidrawRef.current.updateScene({ elements: repaired });
                        return;
                    }
                }
            }
        }

        // Build snapshot from the onChange parameters
        const sanitizedElements = (elements || [])
            .map(DrawingCanvasService.sanitizeElement)
            .filter(Boolean)
            .sort((a, b) => (a?.id || '').localeCompare(b?.id || ''));
        const sanitizedAppState = DrawingCanvasService.sanitizeAppState(appState || {});
        const sanitizedFiles = DrawingCanvasService.sanitizeFiles(files || {});

        const serializeFn = excalidrawPreloadService.getSerializeAsJSON();
        let currentSnapshot;
        if (typeof serializeFn === 'function') {
            try {
                currentSnapshot = serializeFn(sanitizedElements, sanitizedAppState, sanitizedFiles);
            } catch (err) {
                console.error('Failed to serialize drawing snapshot:', err);
                currentSnapshot = JSON.stringify({
                    elements: sanitizedElements,
                    appState: sanitizedAppState,
                    files: sanitizedFiles,
                });
            }
        } else {
            currentSnapshot = JSON.stringify({
                elements: sanitizedElements,
                appState: sanitizedAppState,
                files: sanitizedFiles,
            });
        }

        // First change: capture initial state if not set
        if (!initialSnapshotRef.current) {
            initialSnapshotRef.current = currentSnapshot;
            setIsEdited(false);
            isEditedRef.current = false;
            return;
        }

        // Check if the current state differs from the initial state
        const isNowEdited = currentSnapshot !== initialSnapshotRef.current;
        setIsEdited(isNowEdited);
        isEditedRef.current = isNowEdited;
    }, [isAuthorDrawingBgEditMode, isReadOnly]);

    // Cache background elements from the stored canvas background JSON, if present.
    useEffect(() => {
        const jsonContentItemId = drawingConfig?.canvasBackground?.jsonContentItemId;
        const responseIdentifier = drawingConfig?.responseIdentifier;
        if (!jsonContentItemId || !responseIdentifier) {
            canvasBackgroundElementsRef.current = null;
            return;
        }

        const jsonIdentifier = `${responseIdentifier}_canvasbg_${jsonContentItemId}_jsonData`;
        const raw = userResponses?.[jsonIdentifier]?.[0];
        if (!raw) {
            return;
        }
        try {
            const scene = JSON.parse(raw);
            const normalized = DrawingCanvasService.normaliseBackgroundScene(scene);
            canvasBackgroundElementsRef.current = normalized?.elements || null;
        } catch (err) {
            canvasBackgroundElementsRef.current = null;
        }
    }, [userResponses, drawingConfig?.canvasBackground?.jsonContentItemId, drawingConfig?.responseIdentifier]);

    const showDeleteConfirmation = useCallback(() => {
        setShowDeleteModal(true);
    }, []);

    const deleteDrawing = useCallback(() => {
        setHasDrawing(false);
        setDrawingImageUrl(null);
        setDrawingData(null);
        setShowPreview(false);
        setShowExcalidraw(false);
        if (handleUserResponse && question) {
            const interactionIdentifier = drawingConfig?.responseIdentifier;
            const contentRevisionId = question.contentRevisionId;
            const drawingExtraResponses = DrawingCanvasService.createDeleteDrawingExtraResponses(interactionIdentifier);
            handleUserResponse(contentRevisionId, interactionIdentifier, [], drawingExtraResponses);
        }
    }, [handleUserResponse, question, drawingConfig?.responseIdentifier]);

    const handleDeleteConfirm = useCallback(() => {
        deleteDrawing();
        setShowDeleteModal(false);
    }, [deleteDrawing]);

    const handleCloseConfirm = useCallback(() => {
        postMessageService.postTo(window.parent, { type: 'canvas-background-cancelled' });
    }, []);

    const handleDeleteCancel = useCallback(() => {
        setShowDeleteModal(false);
    }, []);

    const updateDrawingState = useCallback((imageUrl, elements) => {
        setDrawingImageUrl(imageUrl);
        setDrawingData(elements);
    }, []);

    const openExcalidraw = useCallback(async () => {
        initialSnapshotRef.current = null;
        setIsEdited(false);
        isEditedRef.current = false;

        // Load existing JSON data for editing in authorDrawingBgEdit mode
        if (isAuthorDrawingBgEditMode && services) {
            const existingJsonId = DrawingCanvasService.getExistingBackgroundJsonId();
            if (existingJsonId) {
                const existingData = await DrawingCanvasService.loadExistingBackgroundJson(
                    userResponses,
                    drawingConfig?.responseIdentifier,
                    existingJsonId,
                    services
                );
                if (existingData?.elements) {
                    // Store full scene so initialData gets appState (e.g. viewBackgroundColor) on reopen
                    const scene = {
                        elements: existingData.elements,
                        appState: existingData.appState || {},
                        files: existingData.files || {},
                    };
                    pendingDrawingDataRef.current = scene;
                    setDrawingData(existingData.elements);
                    initialSnapshotRef.current = JSON.stringify(scene);
                }
            }
        }

        if (DrawingCanvasService.isOffline()) {
            const offlineData = DrawingCanvasService.loadOfflineDrawingData(userResponses, drawingConfig, services);
            if (offlineData?.imageUrl && !drawingImageUrl?.startsWith('data:')) {
                updateDrawingState(offlineData.imageUrl, offlineData.elements);
            }
        }

        const hasExistingStudentDrawing = DrawingCanvasService.hasExistingDrawing(userResponses, drawingConfig, extraResponses);
        if (!hasExistingStudentDrawing && drawingConfig.canvasBackground?.jsonContentItemId && services) {
            const authorBackground = await DrawingCanvasService.loadAuthorBackgroundForStudent(
                question,
                drawingConfig,
                userResponses,
                drawingConfig.canvasBackground.jsonContentItemId,
                services
            );
            if (authorBackground?.elements) {
                pendingDrawingDataRef.current = authorBackground;
                setDrawingData(authorBackground.elements);
                initialSnapshotRef.current = JSON.stringify(authorBackground);
                canvasBackgroundElementsRef.current = authorBackground.elements;

                if (drawingConfig.canvasBackground?.contentItemId) {
                    const imageUrl = DrawingCanvasService.getDrawingImageUrlFromId(
                        userResponses,
                        drawingConfig.responseIdentifier,
                        drawingConfig.canvasBackground.contentItemId,
                        services
                    );
                    if (imageUrl) {
                        setDrawingImageUrl(imageUrl);
                    }
                }
            }
        }

        loadExcalidraw(true);
    }, [loadExcalidraw, userResponses, drawingConfig, services, drawingImageUrl, updateDrawingState, extraResponses]);

    const loadDrawing = useCallback(async () => {
        initialSnapshotRef.current = null;
        setIsEdited(false);
        isEditedRef.current = false;

        if (!DrawingCanvasService.isOffline()) {
            const existingBase64 = userResponses?.[`${drawingConfig?.responseIdentifier}_drawingBase64`]?.[0];
            const existingJson = userResponses?.[`${drawingConfig?.responseIdentifier}_jsonData`]?.[0];

            const forceFetch = !existingBase64 || !existingJson;

            const presignedData = await DrawingCanvasService.tryPreloadDrawingWithPresignedUrls(
                question,
                drawingConfig,
                userResponses,
                handleUserResponse,
                forceFetch
            );
            if (presignedData) {
                setPendingDrawingScene(presignedData);
                updateDrawingState(presignedData.imageUrl, presignedData.elements);
                loadExcalidraw(true);
                return;
            }

            const s3Data = await DrawingCanvasService.loadAndStoreS3Data(services, question, drawingConfig, handleUserResponse, userResponses);
            if (s3Data) {
                setPendingDrawingScene(s3Data);
                updateDrawingState(s3Data.imageUrl, s3Data.elements);
                loadExcalidraw(true);
                return;
            }
        }

        if (drawingData) {
            loadExcalidraw(true);
            return;
        }
        const offlineData = DrawingCanvasService.loadOfflineDrawingData(userResponses, drawingConfig, services);
        if (offlineData) {
            setPendingDrawingScene(offlineData);
            updateDrawingState(offlineData.imageUrl, offlineData.elements);
        }
        loadExcalidraw(true);
    }, [
        loadExcalidraw,
        drawingData,
        services,
        userResponses,
        drawingConfig,
        question,
        handleUserResponse,
        updateDrawingState,
        setPendingDrawingScene,
    ]);

    const isLoadingDataRef = useRef(false);
    const lastJsonIdRef = useRef(null);
    const lastAutoSaveTimeRef = useRef(null);
    const isEditedRef = useRef(false);

    useEffect(() => {
        if (!hasDrawing) {
            updateDrawingState(null, null);
            isLoadingDataRef.current = false;
            lastJsonIdRef.current = null;
            return;
        }

        let offlineData = DrawingCanvasService.loadOfflineDrawingData(userResponses, drawingConfig, services);
        if (offlineData?.imageUrl) {
            setPendingDrawingScene(offlineData);
            updateDrawingState(offlineData.imageUrl, offlineData.elements);
            isLoadingDataRef.current = false;
            lastJsonIdRef.current = null;
            return;
        }

        if (DrawingCanvasService.isOffline()) {
            updateDrawingState(null, null);
            isLoadingDataRef.current = false;
            lastJsonIdRef.current = null;
            return;
        }

        const pendingSync = userResponses?.[`${drawingConfig?.responseIdentifier}_pendingSync`]?.[0] === 'true';
        if (pendingSync) {
            if (!offlineData && question) {
                const state = storeManager.getState();
                const questionResponse = state.response[question.contentRevisionId];
                if (questionResponse?.responseIdentifiers) {
                    const storeResponses = {};
                    for (const r of questionResponse.responseIdentifiers) {
                        storeResponses[r.identifier] = r.userResponse;
                    }
                    offlineData = DrawingCanvasService.loadOfflineDrawingData(storeResponses, drawingConfig, services);
                }
            }

            if (offlineData?.imageUrl) {
                setPendingDrawingScene(offlineData);
                updateDrawingState(offlineData.imageUrl, offlineData.elements);
            }
            isLoadingDataRef.current = false;
            lastJsonIdRef.current = null;
            return;
        }

        const { imageUrl, jsonUrl, usePresignedUrls } = DrawingCanvasService.resolveDrawingUrls(
            question,
            drawingConfig,
            userResponses,
            services
        );

        const existingBase64InProp = userResponses?.[`${drawingConfig?.responseIdentifier}_drawingBase64`]?.[0];
        const existingJsonInProp = userResponses?.[`${drawingConfig?.responseIdentifier}_jsonData`]?.[0];
        const state = storeManager.getState();
        const questionResponse = state.response[question?.contentRevisionId];
        let existingBase64InState = null;
        let existingJsonInState = null;
        if (questionResponse?.responseIdentifiers) {
            for (const r of questionResponse.responseIdentifiers) {
                if (r.identifier === `${drawingConfig?.responseIdentifier}_drawingBase64`) {
                    existingBase64InState = r.userResponse?.[0];
                }
                if (r.identifier === `${drawingConfig?.responseIdentifier}_jsonData`) {
                    existingJsonInState = r.userResponse?.[0];
                }
            }
        }

        // Set image URL - prefer preloaded base64 from state/props, then resolved URL
        const finalImageUrl = existingBase64InProp || existingBase64InState || imageUrl;
        if (finalImageUrl && finalImageUrl !== drawingImageUrl) {
            setDrawingImageUrl(finalImageUrl);
        }

        // Load JSON data - check if preloaded first, then fetch if needed
        const jsonIdentifier = usePresignedUrls ? jsonUrl : userResponses?.[`${drawingConfig?.responseIdentifier}_jsonId`]?.[0];
        const existingJsonData = existingJsonInProp || existingJsonInState;
        const shouldLoad = !isLoadingDataRef.current && lastJsonIdRef.current !== jsonIdentifier;

        if (shouldLoad && existingJsonData) {
            const jsonDataToUse = existingJsonInProp || existingJsonInState;
            isLoadingDataRef.current = true;
            lastJsonIdRef.current = jsonIdentifier;
            try {
                const scene = JSON.parse(jsonDataToUse);
                setPendingDrawingScene(scene);
                setDrawingData(scene.elements || scene);
                isLoadingDataRef.current = false;
            } catch (err) {
                console.error('Failed to parse drawing JSON data:', err);
                isLoadingDataRef.current = false;
            }
        } else if (shouldLoad && jsonUrl) {
            isLoadingDataRef.current = true;
            lastJsonIdRef.current = jsonIdentifier;

            if (usePresignedUrls) {
                DrawingCanvasService.loadDrawingDataFromPresignedUrl(jsonUrl)
                    .then((data) => {
                        if (data) {
                            setPendingDrawingScene(data);
                            setDrawingData(data.elements);
                        } else {
                            setDrawingData(null);
                        }
                        isLoadingDataRef.current = false;
                    })
                    .catch(() => {
                        setDrawingData(null);
                        isLoadingDataRef.current = false;
                    });
            } else {
                DrawingCanvasService.loadDrawingDataFromUrl(jsonIdentifier, services)
                    .then((data) => {
                        if (data) {
                            setPendingDrawingScene(data);
                            setDrawingData(data.elements);
                        } else {
                            setDrawingData(null);
                        }
                        isLoadingDataRef.current = false;
                    })
                    .catch(() => {
                        setDrawingData(null);
                        isLoadingDataRef.current = false;
                    });
            }
        } else if (!jsonUrl && !existingJsonData) {
            setDrawingData(null);
            isLoadingDataRef.current = false;
            lastJsonIdRef.current = null;
        }
    }, [hasDrawing, userResponses, drawingConfig, services, question, updateDrawingState, setPendingDrawingScene]);


    const saveDrawingLocally = useCallback(async () => {
        if (!excalidrawRef.current || !question) return;

        if (!isEdited) {
            return;
        }

        setIsSavingLocally(true);
        try {
            const result = await DrawingCanvasService.saveDrawingLocally(
                excalidrawRef,
                question,
                drawingConfig,
                isDarkModeEnabled,
                handleUserResponse
            );

            setDrawingImageUrl(result.imageUrl);
            setHasDrawing(true);
            setDrawingData(result.elements);
            setIsSavingLocally(false);

            const snapshot = DrawingCanvasService.captureSnapshot(excalidrawRef);
            if (snapshot) {
                initialSnapshotRef.current = snapshot;
                setIsEdited(false);
                isEditedRef.current = false;
            }

            alertService.showAlert(t('interactions.drawing.drawingSaved'), {
                customFields: {
                    level: AlertLevel.SUCCESS,
                },
            });
        } catch (err) {
            console.error('Failed to save drawing locally:', err);
            setIsSavingLocally(false);
            setHasDrawing(true);
            setDrawingImageUrl(null);
            setDrawingData(excalidrawRef.current.getSceneElements());
        }
    }, [question, drawingConfig, isDarkModeEnabled, handleUserResponse, isEdited]);

    const closeDrawing = useCallback(async () => {
        if (isEdited) {
            showDeleteConfirmation();
        } else {
            postMessageService.postTo(window.parent, { type: 'canvas-background-cancelled' });
        }
    }, [isEdited, showDeleteConfirmation]);

    const saveAndCloseDrawing = useCallback(async () => {
        if (!excalidrawRef.current || !services || !question) return;
        // This bypasses normal save behavior and read-only restrictions
        if (isAuthorDrawingBgEditMode) {
            setIsSaving(true);
            try {
                await DrawingCanvasService.exportDrawingForAuthorBackground(excalidrawRef, question);

                setIsSaving(false);
            } catch (err) {
                console.error('Failed to export drawing for author mode:', err);
                setIsSaving(false);
            }
            return;
        }

        if (!isEdited) {
            setShowExcalidraw(false);
            if (onCanvasStateChange) {
                onCanvasStateChange(false);
            }
            return;
        }

        setIsSaving(true);
        try {
            const result = await DrawingCanvasService.saveDrawingToServer({
                excalidrawRef,
                services,
                question,
                drawingConfig,
                isDarkModeEnabled,
                handleUserResponse,
                isAuthorDrawingBgEdit: false,
            });

            if (result) {
                setDrawingImageUrl(result.imageUrl);
                setHasDrawing(true);
                setDrawingData(result.elements);
                setShowExcalidraw(false);
                setIsSaving(false);

                const snapshot = DrawingCanvasService.captureSnapshot(excalidrawRef);
                if (snapshot) {
                    initialSnapshotRef.current = snapshot;
                    setIsEdited(false);
                    isEditedRef.current = false;
                }

                if (onCanvasStateChange) {
                    onCanvasStateChange(false);
                }
            } else {
                setIsSaving(false);
            }
        } catch (err) {
            console.error('Failed to save drawing:', err);
            setIsSaving(false);
            setHasDrawing(true);
            setDrawingImageUrl(null);
            setDrawingData(excalidrawRef.current.getSceneElements());
            setShowExcalidraw(false);
            if (onCanvasStateChange) {
                onCanvasStateChange(false);
            }
        }
    }, [services, question, drawingConfig, isDarkModeEnabled, handleUserResponse, onCanvasStateChange, isEdited]);

    useEffect(() => {
        if (isSaving || isSavingLocally) {
            return;
        }

        if (!isDrawingEnabled(drawingConfig)) {
            setHasDrawing(false);
            setDrawingData(null);
            setDrawingImageUrl(null);
            setShowPreview(false);
            return;
        }

        const finalHasExistingDrawing = DrawingCanvasService.hasExistingDrawing(userResponses, drawingConfig, extraResponses);

        if (finalHasExistingDrawing) {
            setHasDrawing(true);
        } else {
            setHasDrawing(false);
            setDrawingData(null);
            setDrawingImageUrl(null);
        }
    }, [userResponses, extraResponses, services, isSaving, isSavingLocally, drawingConfig?.responseIdentifier, drawingConfig?.enabled]);

    useEffect(() => {
        const hasCanvasBackgroundToShow = canShowCanvasBackgroundByContext
            && drawingConfig.canvasBackground?.contentItemId;

        if (isGradingQuestionPreviewContext && hasCanvasBackgroundToShow) {
            if (!didAutoShowBackgroundPreviewRef.current) {
                setShowPreview(true);
                didAutoShowBackgroundPreviewRef.current = true;
            }
            didAutoShowDrawingPreviewRef.current = false;
            return;
        }

        if (isReadOnly && hasDrawing) {
            setShowPreview(true);
            didAutoShowDrawingPreviewRef.current = true;
        } else if (hasCanvasBackgroundToShow && !hasDrawing) {
            if (!didAutoShowBackgroundPreviewRef.current) {
                setShowPreview(true);
                didAutoShowBackgroundPreviewRef.current = true;
            }
            didAutoShowDrawingPreviewRef.current = false;
        } else if (hasDrawing && !isReadOnly && !showExcalidraw) {
            if (!didAutoShowDrawingPreviewRef.current) {
                setShowPreview(true);
                didAutoShowDrawingPreviewRef.current = true;
            }
            didAutoShowBackgroundPreviewRef.current = false;
        } else {
            didAutoShowBackgroundPreviewRef.current = false;
            didAutoShowDrawingPreviewRef.current = false;
            setShowPreview(false);
        }
    }, [
        question?.contentRevisionId,
        isReadOnly,
        hasDrawing,
        drawingConfig,
        showExcalidraw,
        isGradingQuestionPreviewContext,
        canShowCanvasBackgroundByContext,
    ]);

    useEffect(() => {
        if (excalidrawRef.current && showExcalidraw) {
            excalidrawRef.current.updateScene({
                appState: {
                    currentItemRoughness: 0,
                    currentItemFontFamily: NORMAL_FONT_ID,
                },
            });
        }
    }, [contrastOption, isDarkModeEnabled, showExcalidraw]);

    useEffect(() => {
        // Auto-open canvas if shouldAutoOpenCanvas is true OR if in authorDrawingBgEdit mode
        const shouldOpen = shouldAutoOpenCanvas || isAuthorDrawingBgEditMode;
        if (shouldOpen && !showExcalidraw && !isReadOnly) {
            openExcalidraw();
        }
        ensureHelpTooltip();
    }, [shouldAutoOpenCanvas, showExcalidraw, isReadOnly]);

    useEffect(() => {
        if (showExcalidraw && excalidrawRef.current) {
            lastAutoSaveTimeRef.current = Date.now();
        }
    }, [showExcalidraw]);

    useEffect(() => {
        // Disable auto-save in authorDrawingBgEdit mode (saves are handled differently)
        if (isAuthorDrawingBgEditMode) {
            return undefined;
        }

        const shouldEnableAutoSave = settingsReader.shouldStoreDrawingsAsUri();
        const autoSaveFrequency = shouldEnableAutoSave ? settingsReader.getDrawingAutoSaveFrequency() : null;

        if (!shouldEnableAutoSave || !autoSaveFrequency) {
            return undefined;
        }

        if (!showExcalidraw) {
            return undefined;
        }

        const checkAndAutoSave = async () => {
            const autoSaveResult = await DrawingCanvasService.checkAndAutoSave({
                excalidrawRef,
                showExcalidraw,
                services,
                question,
                drawingConfig,
                isDarkModeEnabled,
                handleUserResponse,
                isEdited: isEditedRef.current,
                lastSaveTime: lastAutoSaveTimeRef.current,
                autoSaveFrequency,
                isSaving,
                isSavingLocally,
            });

            if (autoSaveResult.shouldSave && autoSaveResult.result) {
                setDrawingImageUrl(autoSaveResult.result.imageUrl);
                setHasDrawing(true);
                setDrawingData(autoSaveResult.result.elements);

                if (autoSaveResult.snapshot) {
                    initialSnapshotRef.current = autoSaveResult.snapshot;
                    setIsEdited(false);
                    isEditedRef.current = false;
                }

                setShowAutoSaveIndicator(true);
                lastAutoSaveTimeRef.current = Date.now();
            } else if (autoSaveResult.reason === 'error') {
                console.error('Auto-save failed:', autoSaveResult.error);
            }
        };

        const intervalId = setInterval(checkAndAutoSave, 10000);

        return () => {
            clearInterval(intervalId);
        };
    }, [showExcalidraw, isSaving, isSavingLocally]);

    useEffect(() => {
        if (showAutoSaveIndicator) {
            const timer = setTimeout(() => {
                setShowAutoSaveIndicator(false);
            }, 3000);

            return () => clearTimeout(timer);
        }
        return undefined;
    }, [showAutoSaveIndicator]);

    useEffect(() => {
        const handleKeyDown = (event) => {
            if (!showExcalidraw) {
                return;
            }

            // Prevent '9' key
            if (event.key === '9') {
                event.preventDefault();
                event.stopPropagation();
            }

            // Prevent Ctrl+K (Windows/Linux) or Cmd+K (Mac) to block link creation
            if ((event.key === 'k' || event.key === 'K') && (event.ctrlKey || event.metaKey)) {
                event.preventDefault();
                event.stopPropagation();
            }
            // Prevent cut, copy, paste for candidate
            if (!isAuthorDrawingBgEditMode && (event.ctrlKey || event.metaKey) && ['c', 'x', 'v'].includes((event.key || '').toLowerCase())) {
                event.preventDefault();
                event.stopPropagation();
            }
            // Prevent Lock/Unlock shortcut (Ctrl+Shift+L / Cmd+Shift+L) for candidate
            if (!isAuthorDrawingBgEditMode && (event.ctrlKey || event.metaKey) && event.shiftKey && (event.key === 'l' || event.key === 'L')) {
                event.preventDefault();
                event.stopPropagation();
            }
        };

        if (showExcalidraw) {
            document.addEventListener('keydown', handleKeyDown, true);
        }

        return () => {
            document.removeEventListener('keydown', handleKeyDown, true);
        };
    }, [showExcalidraw, isAuthorDrawingBgEditMode]);

    const renderStartButton = () => {
        if (isReadOnly && !isGradingQuestionPreviewContext) return null;

        if (showExcalidraw || (hasDrawing && !isGradingQuestionPreviewContext) || shouldAutoOpenCanvas) return null;

        const hasCanvasBackgroundToShow = canShowCanvasBackgroundByContext
            && drawingConfig.canvasBackground?.contentItemId;
        const showPrompt = services?.interactionService?.shouldShowPrompt(drawingConfig?.prompt);

        return (
            <div className={styles.openCanvasBox}>
                <div className={styles.drawingBanner}>
                    <div className={styles.drawingBannerContent}>
                        <div className={styles.drawingBannerLeft}>
                            <div className={styles.drawingBannerTextContainer}>
                                {showPrompt && drawingConfig?.prompt?.content ? (
                                    renderDrawingPrompt(drawingConfig.prompt)
                                ) : (
                                    <span className={styles.drawingBannerText}>
                                        {t('interactions.drawing.toolEnabled')}
                                    </span>
                                )}
                            </div>
                        </div>
                        <div className={styles.drawingBannerRight}>
                            {hasCanvasBackgroundToShow && (
                                <button
                                    type="button"
                                    className={`${styles.drawingActionButton} ${styles.previewButton}`}
                                    onClick={handleTogglePreview}
                                    disabled={isReadOnly}
                                >
                                    <i
                                        className={`fa ${showPreview ? 'fa-eye-slash' : 'fa-eye'} ${styles.drawingActionIcon}`}
                                        aria-hidden="true"
                                    />
                                    {showPreview ? t('interactions.drawing.hidePreview') : t('interactions.drawing.showPreview')}
                                </button>
                            )}
                            <button
                                type="button"
                                className={`${styles.drawingActionButton} ${styles.drawingBannerButton}`}
                                onClick={openExcalidraw}
                                disabled={isReadOnly}
                            >
                                <i className="fa fa-pencil" aria-hidden="true" />
                                {t('interactions.drawing.startDrawing')}
                            </button>
                        </div>
                    </div>
                    {hasCanvasBackgroundToShow && showPreview && <div className={styles.drawingSeparator} />}
                    {hasCanvasBackgroundToShow && showPreview && renderDrawingPreview()}
                </div>
            </div>
        );
    };

    const renderDrawingPreview = () => {
        if (!showPreview) return null;
        const isDeliveryContext = (context === sessionConstants.CONTEXT_VIEW_DELIVERY);
        const printPreview = isDeliveryContext ? styles.drawingPrint : styles.drawingPreview;

        let previewImageUrl = isGradingQuestionPreviewContext ? null : drawingImageUrl;

        if (!isGradingQuestionPreviewContext && !previewImageUrl?.startsWith('data:')) {
            let offlineData = DrawingCanvasService.loadOfflineDrawingData(userResponses, drawingConfig, services);

            if (!offlineData && question) {
                const state = storeManager.getState();
                const questionResponse = state.response[question.contentRevisionId];
                if (questionResponse?.responseIdentifiers) {
                    const storeResponses = {};
                    for (const r of questionResponse.responseIdentifiers) {
                        storeResponses[r.identifier] = r.userResponse;
                    }
                    offlineData = DrawingCanvasService.loadOfflineDrawingData(storeResponses, drawingConfig, services);
                }
            }

            if (offlineData?.imageUrl) {
                previewImageUrl = offlineData.imageUrl;
            }
        }

        const canShowCanvasBackground = canShowCanvasBackgroundByContext
            && drawingConfig.canvasBackground?.contentItemId && services;
        if (!previewImageUrl && canShowCanvasBackground) {
            previewImageUrl = DrawingCanvasService.getDrawingImageUrlFromId(
                userResponses,
                drawingConfig.responseIdentifier,
                drawingConfig.canvasBackground.contentItemId,
                services
            );
        }

        return (
            <div className={printPreview}>
                {previewImageUrl ? (
                    <img
                        src={previewImageUrl}
                        alt="Drawing preview"
                        className={styles.drawingPreviewImage}
                    />
                ) : null}
                {!previewImageUrl && (
                    <div className={styles.drawingPreviewPlaceholder}>
                        <i className={`fa fa-image ${styles.drawingPreviewPlaceholderIcon}`} />
                        <div style={{ marginTop: '0.5rem' }}>
                            <strong>{t('interactions.drawing.drawingPreview')}</strong>
                        </div>
                        <div style={{ marginTop: '0.25rem', fontSize: '0.85em' }}>
                            {t('interactions.drawing.drawingSavedSuccessfully')}
                        </div>
                        <div style={{ marginTop: '0.25rem', fontSize: '0.8em', color: '#999' }}>
                            {t('interactions.drawing.drawingElementsCount', { count: drawingData?.length || 0 })}
                        </div>
                    </div>
                )}
            </div>
        );
    };

    const handleTogglePreview = useCallback(() => {
        setShowPreview(prev => !prev);
    }, []);

    const handleOpenDrawingTab = useCallback(() => {
        postMessageService.postTo(window.parent, {
            type: 'player:switch-to-drawing-tab',
        });
    }, []);

    const renderReadOnlyGradingControls = () => {
        return (
            <div className={styles.openCanvasBox}>
                <div className={styles.drawingBanner}>
                    <div className={styles.drawingBannerContent}>
                        <div className={styles.drawingBannerLeft}>
                            <i className={`fa fa-pencil ${styles.drawingBannerIcon}`} />
                            <span className={styles.drawingBannerText}>
                                {t('interactions.drawing.drawingAttached')}
                            </span>
                        </div>
                        <div className={styles.drawingBannerRight}>
                            <button
                                type="button"
                                className={`${styles.drawingActionButton} ${styles.drawingBannerButton} ${styles.openDrawingTabButton}`}
                                onClick={handleOpenDrawingTab}
                            >
                                {t('interactions.drawing.openDrawingTab')}
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    const renderReadOnlyControls = () => {
        return (
            <div className={styles.drawingSavedContainer}>
                <div className={styles.drawingSavedHeader}>
                    <div className={styles.drawingBannerLeft}>
                        <i className={`fa fa-pencil ${styles.drawingBannerIcon}`} />
                        <span className={styles.drawingBannerText}>
                            {t('interactions.drawing.drawingAttached')}
                        </span>
                    </div>
                </div>
                {renderDrawingPreview()}
            </div>
        );
    };

    const renderDrawingPrompt = (prompt) => {
        if (!prompt?.content) {
            return null;
        }

        const classNames = ['interaction-prompt'];
        if (prompt.isRichText) {
            classNames.push('rich-text-prompt');
        }

        return (
            <div className={classNames.join(' ')}>
                <div className="text" dangerouslySetInnerHTML={{ __html: prompt.content }} />
            </div>
        );
    };

    const renderEditableControls = () => {
        const showPrompt = services?.interactionService?.shouldShowPrompt(drawingConfig?.prompt);
        return (
            <div className={styles.openCanvasBox}>
                <div className={styles.drawingBanner}>
                    <div className={styles.drawingSavedHeader}>
                        <div className={styles.drawingSavedLeft}>
                            {showPrompt && drawingConfig?.prompt?.content ? (
                                renderDrawingPrompt(drawingConfig.prompt)
                            ) : (
                                <span className={styles.drawingSavedStatus}>
                                    {t('interactions.drawing.drawingSaved')}
                                </span>
                            )}
                        </div>
                        <div className={styles.drawingSavedActions}>
                            <button
                                type="button"
                                className={`${styles.drawingActionButton} ${styles.previewButton}`}
                                onClick={handleTogglePreview}
                                disabled={isReadOnly}
                            >
                                <i
                                    className={`fa ${showPreview ? 'fa-eye-slash' : 'fa-eye'} ${styles.drawingActionIcon}`}
                                    aria-hidden="true"
                                />
                                {showPreview ? t('interactions.drawing.hideDrawing') : t('interactions.drawing.showDrawing')}
                            </button>
                            <button
                                type="button"
                                className={`${styles.drawingActionButton} ${styles.editButton}`}
                                onClick={loadDrawing}
                                disabled={isReadOnly}
                            >
                                <i className={`fa fa-pencil ${styles.drawingActionIcon}`} aria-hidden="true" />
                                {t('interactions.drawing.editDrawing')}
                            </button>
                            <button
                                type="button"
                                className={`${styles.drawingActionButton} ${styles.deleteButton}`}
                                onClick={showDeleteConfirmation}
                                disabled={isReadOnly}
                            >
                                <i className={`fa fa-trash ${styles.drawingActionIcon}`} aria-hidden="true" />
                                {t('interactions.drawing.delete')}
                            </button>
                        </div>
                    </div>
                    {showPreview ? <div className={styles.drawingSeparator} /> : <></>}
                    {renderDrawingPreview()}
                </div>
            </div>
        );
    };

    const renderDrawingControls = () => {
        const hasCanvasBackgroundToShow = canShowCanvasBackgroundByContext
            && drawingConfig.canvasBackground?.contentItemId;

        if (showExcalidraw || (!hasDrawing && !hasCanvasBackgroundToShow)) return null;

        if (isReadOnly && (context === sessionConstants.CONTEXT_VIEW_DELIVERY)) {
            return <div>{renderDrawingPreview()}</div>;
        }

        if (isReadOnly && isGradingQuestionPreviewContext) {
            return null;
        }

        if (isReadOnly) {
            const pathname = window?.parent?.location?.pathname;
            const isGradingContext = pathname?.startsWith('/grading');
            return isGradingContext ? renderReadOnlyGradingControls() : renderReadOnlyControls();
        }

        if (hasCanvasBackgroundToShow && !hasDrawing) {
            return null;
        }

        return renderEditableControls();
    };

    const excalidrawLangCode = useMemo(() => {
        const currentLanguage = sessionLanguage || i18next.language || 'en-US';
        return DrawingCanvasService.getExcalidrawLanguageCode(currentLanguage);
    }, [sessionLanguage, i18next.language]);

    const renderExcalidrawEditor = () => {
        if (!showExcalidraw) return null;

        return (
            <div className={styles.excalidrawContainer}>
                <div className={styles.excalidrawHeader}>
                    <div className={styles.excalidrawHeaderTop}>
                        {showQuestionToggle && (
                            <div className={styles.excalidrawHeaderLeft}>
                                {showQuestionToggle}
                            </div>
                        )}
                        <div className={styles.excalidrawActions}>
                            {!isReadOnly && (
                                <button
                                    type="button"
                                    className={`${styles.excalidrawButton} ${styles.saveButton} ${styles.hiddenButton} btn main-btn`}
                                    onClick={saveDrawingLocally}
                                    disabled={isSaving || isSavingLocally}
                                >
                                    {t('interactions.drawing.save')}
                                </button>
                            )}
                            {showAutoSaveIndicator && (
                                <div className={styles.autoSaveIndicator}>
                                    <i className="fa fa-check-circle" aria-hidden="true" />
                                    <span>{t('interactions.drawing.changesSaved')}</span>
                                </div>
                            )}

                            {isAuthorDrawingBgEditMode && (
                                <button
                                    type="button"
                                    className={`${styles.excalidrawButton} ${styles.closeButton} btn secondary-btn`}
                                    onClick={closeDrawing}
                                    disabled={isSaving}
                                >
                                    {t('interactions.drawing.close')}
                                </button>
                            )}
                            {((isAuthorDrawingBgEditMode && isEdited) || !isAuthorDrawingBgEditMode) && (
                                <button
                                    type="button"
                                    className={`${styles.excalidrawButton} ${styles.closeButton} btn main-btn`}
                                    onClick={saveAndCloseDrawing}
                                    disabled={isSaving || isSavingLocally}
                                >
                                    {isSaving && <i className="fa fa-spinner fa-pulse" aria-hidden="true" />}
                                    {(() => {
                                        if (isSaving) {
                                            return t('interactions.drawing.saving');
                                        }
                                        return t('interactions.drawing.saveAndclose');
                                    })()}
                                </button>
                            )}
                        </div>
                    </div>
                    {questionContent && (
                        <div className={styles.excalidrawHeaderQuestion}>
                            {questionContent}
                        </div>
                    )}
                </div>
                <div className={styles.excalidrawWrapper}>
                    <div
                        ref={canvasContainerRef}
                        className={styles.canvasContainer}
                    >
                        <div className={styles.canvasInner}>
                            {isLazyExcalidrawLoaded && excalidrawComponent ? (
                                <Suspense fallback={<div>{t('interactions.drawing.loadingExcalidraw')}</div>}>
                                    {React.createElement(excalidrawComponent, {
                                        key: `excalidraw-${question?.contentRevisionId}`,
                                        ref: excalidrawRef,
                                        initialData: (() => {
                                            const pendingAppState = pendingDrawingDataRef.current?.appState || {};
                                            // Ensure collaborators is always an array or removed
                                            const sanitizedAppState = { ...pendingAppState };
                                            if (sanitizedAppState.collaborators !== undefined) {
                                                if (Array.isArray(sanitizedAppState.collaborators)) {
                                                    // Keep it as is if it's already an array
                                                } else {
                                                    // Remove it if it's not an array to prevent errors
                                                    delete sanitizedAppState.collaborators;
                                                }
                                            }
                                            return {
                                                elements: drawingData || pendingDrawingDataRef.current?.elements || [],
                                                appState: {
                                                    ...sanitizedAppState,
                                                    currentItemRoughness: 0,
                                                    currentItemFontFamily: NORMAL_FONT_ID,
                                                },
                                                files: pendingDrawingDataRef.current?.files || {},
                                            };
                                        })(),
                                        viewModeEnabled: isAuthorDrawingBgEditMode ? false : isReadOnly,
                                        zenModeEnabled: false,
                                        gridModeEnabled: false,
                                        theme: isDarkModeEnabled ? 'dark' : 'light',
                                        langCode: excalidrawLangCode,
                                        detectScroll: true,
                                        autoFocus: false,
                                        onReady: handleExcalidrawReady,
                                        onChange: handleExcalidrawChange,
                                        UIOptions: {
                                            welcomeScreen: false,
                                        },
                                    })}
                                </Suspense>
                            ) : null}
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    if (!isDrawingEnabled(drawingConfig)) {
        return null;
    }

    const hasCanvasBackgroundToShow = canShowCanvasBackgroundByContext
        && drawingConfig.canvasBackground?.contentItemId;

    if (isReadOnly && !hasDrawing && !hasCanvasBackgroundToShow) {
        return null;
    }

    if (context === sessionConstants.CONTEXT_VIEW_GRADING_QUESTION_DELIVERY && !hasDrawing) {
        return null;
    }

    return (
        <div className={`${styles.drawingBlockContainer} ${isAuthorDrawingBgEditMode ? styles.drawingBlockContainerAuthorMode : ''}`}>
            {renderStartButton()}
            {renderDrawingControls()}
            {showExcalidraw && (
                <div className={styles.drawingBlockWrapper}>
                    {renderExcalidrawEditor()}
                </div>
            )}
            <DrawingDeleteConfirmModal
                isAuthorDrawingBgEdit={isAuthorDrawingBgEditMode}
                isOpen={showDeleteModal}
                onClose={isAuthorDrawingBgEditMode ? saveAndCloseDrawing : handleDeleteCancel}
                onConfirm={isAuthorDrawingBgEditMode ? handleCloseConfirm : handleDeleteConfirm}
                onDismiss={handleDeleteCancel}
                isSaving={isSaving}
            />
        </div>
    );
};

DrawingBlock.propTypes = {
    question: PropTypes.object.isRequired,
    userResponses: PropTypes.oneOfType([
        PropTypes.object,
        PropTypes.array,
    ]),
    handleUserResponse: PropTypes.func,
    services: PropTypes.object,
    isReadOnly: PropTypes.bool,
    onCanvasStateChange: PropTypes.func,
    contrastOption: PropTypes.string,
    language: PropTypes.string,
    context: PropTypes.string,
    shouldAutoOpenCanvas: PropTypes.bool,
    showQuestionToggle: PropTypes.node,
    questionContent: PropTypes.node,
};

const mapStateToProps = ({ ui, session }) => {
    const { contrastOption = contrastOptions.NORMAL } = ui;
    const { language } = session || {};
    return { contrastOption, language };
};

const DrawingBlockContainer = connect(mapStateToProps)(DrawingBlock);

export { DrawingBlock };
export default DrawingBlockContainer;

